import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  createContactLabel,
  deleteContactLabel,
  listContactLabels,
  renameContactLabel,
} from '../../api/business/endpoints';
import type { ContactLabel } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice } from '../../components/ErrorNotice';
import { FormDialog } from '../../components/FormDialog';
import { useT } from '../../i18n/LanguageContext';

const AFFECTED: Array<string> = ['contact-labels', 'contacts', 'contact'];

/** The company's contact labels (R0039 D-04): added, renamed, deleted — each with how many contacts carry it. */
export const ContactLabelsDialog = ({ companyId, onClose }: { companyId: string; onClose: () => void }) => {
  const t = useT();
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<ContactLabel | null>(null);
  const [newName, setNewName] = useState('');
  const [deleting, setDeleting] = useState<ContactLabel | null>(null);
  const labels = useQuery({
    queryKey: ['contact-labels', companyId],
    queryFn: () => listContactLabels(companyId),
  });

  const create = useAppMutation(
    (label: string) => createContactLabel(companyId, label),
    AFFECTED,
    () => setName(''),
  );
  const rename = useAppMutation(
    (label: ContactLabel) => renameContactLabel(companyId, label.id, newName),
    AFFECTED,
    () => setRenaming(null),
  );
  const remove = useAppMutation(
    (label: ContactLabel) => deleteContactLabel(companyId, label.id),
    AFFECTED,
    () => setDeleting(null),
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('contactLabels.title')}</DialogTitle>
      <DialogContent>
        <ErrorNotice error={labels.error ?? create.error} onRetry={() => void labels.refetch()} />
        <Stack
          component="form"
          direction="row"
          spacing={1}
          sx={{ pt: 1, mb: 1 }}
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(name);
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            label={t('contactLabels.new')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 80 }}
          />
          <Button type="submit" variant="contained" disabled={name.trim() === '' || create.isPending}>
            {t('contactLabels.add')}
          </Button>
        </Stack>
        {labels.data && labels.data.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('contactLabels.empty')}
          </Typography>
        ) : null}
        <List dense disablePadding>
          {(labels.data ?? []).map((label) => (
            <ListItem
              key={label.id}
              disableGutters
              secondaryAction={
                <>
                  <Tooltip title={t('contactLabels.rename')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewName(label.name);
                        setRenaming(label);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('contactLabels.deleteTitle')}>
                    <IconButton size="small" onClick={() => setDeleting(label)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <ListItemText
                primary={label.name}
                secondary={t('contactLabels.count', { count: label.contactCount })}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
      {renaming !== null ? (
        <FormDialog
          title={t('contactLabels.rename')}
          onClose={() => setRenaming(null)}
          onSubmit={() => rename.mutate(renaming)}
          submitLabel={t('common.save')}
          isBusy={rename.isPending || newName.trim() === ''}
          error={rename.error}
          maxWidth="xs"
        >
          <TextField
            autoFocus
            label={t('contactLabels.name')}
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            inputProps={{ maxLength: 80 }}
          />
        </FormDialog>
      ) : null}
      {deleting !== null ? (
        <ConfirmDialog
          title={t('contactLabels.deleteTitle')}
          message={t('contactLabels.deleteMessage', { name: deleting.name })}
          confirmLabel={t('contact.delete')}
          onConfirm={() => remove.mutate(deleting)}
          onClose={() => setDeleting(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </Dialog>
  );
};
